import { z } from "zod";
import { pageOf, pageQuerySchema } from "../common/pagination.js";
import { localDateSchema, moneySchema, quantitySchema, timestampSchema, uuidSchema, } from "../common/primitives.js";
import { productUnitSchema } from "../masterdata/products.js";
import { productSupplierSchema, stationProductSettingSchema } from "./catalog.js";
import { expiryStateSchema, lotNoSchema, positiveQuantitySchema, stockMovementTypeSchema, marketUnitCostSchema, } from "./enums.js";
/**
 * Stok defteri okuma uçları ve açılış stoğu (Prompt 16, ADR 0016 §3–6).
 *
 * Bakiye = depo × ürün: miktar (negatif olabilir), DEĞER (kuruş) ve türetilmiş ortalama birim maliyet (₺ 6 ondalık).
 * Hareketler append-only'dir; her hareket kendinden sonraki miktarı ve değeri taşır (`quantityAfter`, `valueAfter`).
 */
// ── Bakiyeler ────────────────────────────────────────────────────────────────
export const stockBalanceSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    stationId: uuidSchema,
    stationName: z.string(),
    warehouseId: uuidSchema,
    warehouseCode: z.string(),
    warehouseName: z.string(),
    productId: uuidSchema,
    productCode: z.string(),
    productName: z.string(),
    categoryId: uuidSchema.nullable(),
    categoryName: z.string().nullable(),
    unit: productUnitSchema,
    trackExpiry: z.boolean(),
    quantity: quantitySchema.describe("Eldeki miktar; negatif olabilir (kasa satışı reddedilmez)"),
    value: moneySchema.describe("Stok değeri (kuruş)"),
    averageUnitCost: marketUnitCostSchema.describe("Hareketli ortalama birim maliyet (₺); eldeki ≤ 0 ise son bilinen"),
    minStock: quantitySchema.nullable(),
    maxStock: quantitySchema.nullable(),
    shelfLocation: z.string().nullable(),
    isListed: z.boolean().nullable().describe("İstasyon ürün ayarı yoksa null"),
    belowMin: z.boolean().describe("Asgari stoğun altında"),
    isNegative: z.boolean(),
    lastReceiptAt: timestampSchema.nullable(),
    lastSaleAt: timestampSchema.nullable(),
    lastCountedAt: timestampSchema.nullable(),
});
export const stockBalancePageSchema = pageOf(stockBalanceSchema);
export const listStockBalancesQuerySchema = pageQuerySchema.extend({
    companyId: uuidSchema,
    stationId: uuidSchema.optional(),
    warehouseId: uuidSchema.optional(),
    productId: uuidSchema.optional(),
    categoryId: uuidSchema.optional(),
    search: z.string().trim().max(100).optional().describe("Ürün adı, kodu ya da barkodu"),
    belowMin: z.enum(["true", "false"]).optional().describe("Yalnızca asgari stoğun altındakiler"),
    negative: z.enum(["true", "false"]).optional().describe("Yalnızca negatif bakiyeler"),
    includeZero: z.enum(["true", "false"]).optional().describe("Sıfır bakiyeleri de getir (varsayılan: evet)"),
});
// ── Hareketler ───────────────────────────────────────────────────────────────
export const stockMovementSchema = z.object({
    id: uuidSchema,
    stationId: uuidSchema,
    warehouseId: uuidSchema,
    warehouseCode: z.string(),
    productId: uuidSchema,
    productCode: z.string(),
    productName: z.string(),
    unit: productUnitSchema.describe("Ürünün satış birimi (miktarın birimi)"),
    lotId: uuidSchema.nullable(),
    lotNo: z.string().nullable(),
    expiryDate: localDateSchema.nullable(),
    movementType: stockMovementTypeSchema,
    quantity: quantitySchema.describe("Giriş +, çıkış −"),
    value: moneySchema.describe("Kuruş; giriş +, çıkış −"),
    unitCost: marketUnitCostSchema,
    quantityAfter: quantitySchema,
    valueAfter: moneySchema,
    occurredAt: timestampSchema,
    businessDate: localDateSchema,
    sourceModule: z.string(),
    sourceDocType: z.string(),
    sourceDocId: uuidSchema,
    sourceLineRef: z.string(),
    dailyClosingId: uuidSchema.nullable().describe("SMM gün kapanışına girdiyse kapanış"),
    createdAt: timestampSchema,
});
export const stockMovementPageSchema = pageOf(stockMovementSchema);
export const listStockMovementsQuerySchema = pageQuerySchema.extend({
    companyId: uuidSchema,
    stationId: uuidSchema.optional(),
    warehouseId: uuidSchema.optional(),
    productId: uuidSchema.optional(),
    movementType: stockMovementTypeSchema.optional(),
    sourceDocId: uuidSchema.optional().describe("Belgenin (satış, transfer, fire, sayım…) hareketleri"),
    businessDateFrom: localDateSchema.optional(),
    businessDateTo: localDateSchema.optional(),
});
// ── Partiler (SKT) ───────────────────────────────────────────────────────────
export const stockLotSchema = z.object({
    id: uuidSchema,
    stationId: uuidSchema,
    warehouseId: uuidSchema,
    warehouseCode: z.string(),
    productId: uuidSchema,
    productCode: z.string(),
    productName: z.string(),
    unit: productUnitSchema.describe("Ürünün satış birimi (miktarın birimi)"),
    lotNo: z.string().nullable(),
    expiryDate: localDateSchema.nullable(),
    expiryState: expiryStateSchema.nullable().describe("SKT'siz partide null"),
    daysToExpiry: z.number().int().nullable().describe("Negatif = geçmiş"),
    receivedAt: timestampSchema,
    initialQuantity: quantitySchema,
    quantity: quantitySchema.describe("Kalan miktar (asla negatif değil)"),
    sourceDocType: z.string(),
    sourceDocId: uuidSchema,
});
export const stockLotPageSchema = pageOf(stockLotSchema);
export const listStockLotsQuerySchema = pageQuerySchema.extend({
    companyId: uuidSchema,
    stationId: uuidSchema.optional(),
    warehouseId: uuidSchema.optional(),
    productId: uuidSchema.optional(),
    expiryState: expiryStateSchema.optional(),
    includeEmpty: z.enum(["true", "false"]).optional().describe("Tükenmiş partileri de getir"),
});
// ── Ürün stok kartı ──────────────────────────────────────────────────────────
export const productStockCardQuerySchema = z.object({
    companyId: uuidSchema,
    stationId: uuidSchema.optional().describe("Verilirse yalnızca bu istasyonun depoları"),
});
export const productStockCardSchema = z.object({
    productId: uuidSchema,
    productCode: z.string(),
    productName: z.string(),
    unit: productUnitSchema,
    trackExpiry: z.boolean(),
    totals: z.object({ quantity: quantitySchema, value: moneySchema }),
    balances: z.array(stockBalanceSchema),
    lots: z.array(stockLotSchema).describe("Açık (kalanı olan) partiler, FEFO sırasıyla"),
    recentMovements: z.array(stockMovementSchema).describe("Son 20 hareket"),
    suppliers: z.array(productSupplierSchema),
    stationSettings: z.array(stationProductSettingSchema),
});
// ── Açılış stoğu ─────────────────────────────────────────────────────────────
export const openingStockLineSchema = z.object({
    productId: uuidSchema,
    quantity: positiveQuantitySchema,
    unitCost: marketUnitCostSchema.describe("KDV hariç birim maliyet (₺)"),
    lotNo: lotNoSchema.nullable().default(null).describe("SKT takipli üründe isteğe bağlı parti no"),
    expiryDate: localDateSchema.nullable().default(null),
});
/**
 * Açılış stoğu: depoda HİÇ hareketi olmayan ürünlerin başlangıç miktarı ve maliyeti. Muhasebe fişi ÜRETMEZ — 153'ün
 * açılışı muhasebe açılış fişindedir (akaryakıt kalıbı, ADR 0012).
 */
export const createOpeningStockSchema = z.object({
    warehouseId: uuidSchema,
    businessDate: localDateSchema.optional().describe("Açılış iş günü; verilmezse bugün"),
    lines: z
        .array(openingStockLineSchema)
        .min(1)
        .max(2000)
        .refine((lines) => new Set(lines.map((line) => line.productId)).size === lines.length, "Aynı ürün iki satırda olamaz"),
});
export const openingStockResultSchema = z.object({
    batchId: uuidSchema.describe("Hareketlerin kaynak belge kimliği"),
    warehouseId: uuidSchema,
    businessDate: localDateSchema,
    lineCount: z.number().int(),
    totalValue: moneySchema,
    lines: z.array(z.object({
        productId: uuidSchema,
        quantity: quantitySchema,
        value: moneySchema,
        lotId: uuidSchema.nullable(),
    })),
});
//# sourceMappingURL=stock.js.map